'use client';

import type { FC, ReactNode } from 'react';
import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { formatCreditsShort } from '../../utils/formatters';
import { ScreenContent, ScreenFixedBottom, ScreenDisableSafeArea } from '../../screen';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Title, Text, BlurImage, OverlayBadge, SegmentedControl } from '../../components/ui';
import { CaseDetailsScreenSkeleton } from './CaseDetailsScreenSkeleton';
import { Button } from '../../components/ui/Button';
import { ButtonGroup } from '../../components/ui';
import { ItemListItem } from '../../components/ItemListItem';
import { Credits } from '../../components/Credits';
import { BonusCredits } from '../../components/BonusCredits';
import { CaseAffordabilityCta } from '../../components/CaseAffordabilityCta';
import '../BandScreen/BandNeon.css';
import { ItemPrice, useSteamPriceAllowed } from '../../components/ItemPrice';
import { useNavigation } from '../../contexts/NavigationContext';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useWikiItemHref } from '../../hooks/useWikiItemHref';
import { useCaseLinkHref } from '../../hooks/useCaseLinkHref';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import type { CaseItem } from '../../services/generated/Api';
import { useCaseDetails, useCasesCatalogQuery } from '../../queries/useCasesQuery';
import { CaseCard } from '../../components/cases/CaseCard';
import type { CatalogCase } from '../../components/cases/catalog/catalog.types';
import { similarCases } from '../../components/cases/catalog/similarCases';
import { WikiLink } from '../wiki/components/WikiLink';
import { useUiMotionEnabled } from '../../hooks/useMotion';
import { useCaseAffordability } from '../../hooks/useCaseAffordability';
import { useCaseQtyStore, getStoredCaseQty } from '../../stores/caseQtyStore';
import translations from './CaseDetailsScreen.translations';
import { GiOpenChest } from '../../components/icons';

interface CaseDetailsScreenProps {
  params?: { caseId?: string };
  seoSlot?: ReactNode;
  seoHeading?: string;
  // WHY: на сайте подборку считает сервер — весь каталог в HTML каждой страницы кейса не кладём; без неё (Mini App)
  // экран считает подборку сам из каталога
  similar?: CatalogCase[];
}

const QTYS = [1, 2, 3, 5, 10] as const;

// WHY: ширина карточки по сетке подборки: десктоп — 4 колонки в колонке 896 px, телефон — 2 колонки во всю ширину
const SIMILAR_IMAGE_SIZES = '(min-width: 1024px) 208px, 50vw';

export const CaseDetailsScreen: FC<CaseDetailsScreenProps> = ({ params, seoSlot, seoHeading, similar: similarFromServer }) => {
  const { t } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const wikiHref = useWikiItemHref();
  const caseLink = useCaseLinkHref();
  const steamPriceAllowed = useSteamPriceAllowed();
  const scrollRef = useRef<HTMLDivElement>(null);
  const isDesktop = useIsDesktop();

  const caseId = params?.caseId ?? '';
  const [qty, setQtyState] = useState(() => getStoredCaseQty(caseId));
  const persistQty = useCaseQtyStore((s) => s.setQty);
  const setQty = (n: number) => {
    setQtyState(n);
    persistQty(caseId, n);
  };

  const {
    data: caseData,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useCaseDetails(caseId);

  const error = !!queryError;

  const { data: catalog } = useCasesCatalogQuery(similarFromServer === undefined);
  const similar = useMemo(
    () => similarFromServer ?? (catalog && caseData ? similarCases(catalog, caseData) : []),
    [similarFromServer, catalog, caseData],
  );
  const openSimilar = useCallback((id: string) => navigation.navigate('case-details', { caseId: id }), [navigation]);
  // WHY: в стеке Mini App под открытым кейсом остаются смонтированными предыдущие — постоянный id задвоился бы
  const similarTitleId = useId();

  const motionEnabled = useUiMotionEnabled();

  // WHY: хук зовётся в теле экрана: ScreenFixedBottom рендерит панель через layout-провайдер вне поддерева экрана
  const affordability = useCaseAffordability({
    caseId: caseData?.id,
    openCurrency: caseData?.openCurrency,
    priceInCredits: caseData?.priceInCredits,
    qty,
  });
  const { isBonusCase, hasFreeOpen, availableBalance, cost: openCost, canAfford } = affordability;

  useEffect(() => {
    if (qty === 1 || canAfford(qty)) return;
    const next = [...QTYS].reverse().find((n) => canAfford(n)) ?? 1;
    setQty(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qty, canAfford]);

  const handleOpenCase = () => {
    if (!caseData) return;
    if (qty <= 1) {
      navigation.navigate('case-roulette', { caseId: caseData.id, autoStart: true });
    } else {
      navigation.navigate('case-honeycomb', { caseId: caseData.id, count: qty });
    }
  };

  const qtySegments = QTYS.map((n) => ({
    id: n,
    label: (
      <span className="flex items-center">
        <span className="mr-px text-[11px] text-white/40">×</span>
        {n}
      </span>
    ),
    disabled: n !== 1 && !canAfford(n),
  }));

  const qtySelector = caseData ? (
    <SegmentedControl<number> segments={qtySegments} value={qty} onChange={setQty} size="md" />
  ) : null;

  const balanceRow =
    availableBalance !== undefined ? (
      <div className="flex justify-center">
        <OverlayBadge blur className="gap-1.5 text-sm">
          <span className="text-white/60">{t('balance')}</span>
          {isBonusCase ? <BonusCredits amount={availableBalance} /> : <Credits amount={availableBalance} />}
        </OverlayBadge>
      </div>
    ) : null;

  const priceTag = (amount: number, onNeon = false) =>
    isBonusCase ? <BonusCredits amount={amount} iconClassName={onNeon ? 'text-white' : undefined} /> : <Credits amount={amount} />;
  const openBtnClassName = isBonusCase ? 'bnc-btn-neon' : undefined;
  const ctaLabel =
    hasFreeOpen && openCost === 0 ? (
      t('freeOpenButton')
    ) : (
      <>
        {t('openButton')}{' '}
        <span
          key={openCost}
          className={`inline-flex items-center${motionEnabled ? ' animate-shot-price-swap' : ''}`}
        >
          {priceTag(openCost, true)}
        </span>
      </>
    );

  const openCta = !caseData ? null : affordability.isBlocked ? (
    <CaseAffordabilityCta
      affordability={affordability}
      onJoinClick={() => navigation.navigate('band')}
      onTopupClick={() => navigation.navigate('payment')}
    />
  ) : (
    <Button onClick={handleOpenCase} variant="cta" size="l" stretched className={openBtnClassName} data-sound="cta">
      {ctaLabel}
      <span style={{ marginLeft: '8px', opacity: 0.7 }}>+{caseData.xpReward * qty} XP</span>
    </Button>
  );

  const sortedItems = caseData?.items
    ? [...caseData.items].sort((a, b) => b.priceInCredits - a.priceInCredits)
    : [];

  // WHY: карточка предмета — ссылка на его страницу в вики, чтобы переход видел и робот; в Mini App тот же мягкий переход
  // Next открывает вики слоем над стеком
  const itemCards = sortedItems.map((item: CaseItem, index: number) => {
    const key = `${item.hashName}-${index}`;
    const href = wikiHref(item.wikiPath);
    const card = (
      <ItemListItem
        item={{
          name: item.name,
          imageUrl: item.imageUrl,
          rarityColor: item.rarityColor,
        }}
        className={href ? undefined : 'cursor-default'}
        topRightContent={
          item.chancePercent ? (
            <OverlayBadge>
              <span className="text-xs font-semibold text-accent-green">{item.chancePercent}%</span>
            </OverlayBadge>
          ) : undefined
        }
      >
        <OverlayBadge>
          <ItemPrice credits={item.priceInCredits} variant="cardPrice" />
        </OverlayBadge>
        {steamPriceAllowed && item.steamPrice != null && (
          <OverlayBadge>
            <ItemPrice steamPrice={item.steamPrice} steamIconSize={11} variant="steamPrice" />
          </OverlayBadge>
        )}
      </ItemListItem>
    );
    return href ? (
      <WikiLink key={key} href={href} className="block rounded-[21px]">
        {card}
      </WikiLink>
    ) : (
      <Fragment key={key}>{card}</Fragment>
    );
  });

  const similarBlock =
    similar.length > 0 ? (
      <section aria-labelledby={similarTitleId} className="flex flex-col gap-4">
        <Title id={similarTitleId} level="2" className="text-lg font-semibold text-white m-0 text-center">
          {t('similarCases')}
        </Title>
        <div className={isDesktop ? 'grid grid-cols-4 gap-3' : 'grid grid-cols-2 gap-2'}>
          {similar.map((c) => (
            <CaseCard
              key={c.id}
              id={c.id}
              name={c.name}
              imageUrl={c.imageUrl}
              imagePlaceholder={c.imagePlaceholder}
              priceInCredits={c.priceInCredits}
              openingsCount={c.openingsCount}
              isNew={c.isNew}
              isPopular={c.isPopular}
              href={caseLink(c)}
              onClick={openSimilar}
              imageSizes={SIMILAR_IMAGE_SIZES}
            />
          ))}
        </div>
      </section>
    ) : null;

  if (loading) {
    return <CaseDetailsScreenSkeleton />;
  }

  if (error && !caseData) {
    return (
      <ScreenContent className="flex items-center justify-center min-h-[300px] p-4">
        <ErrorMessage onRetry={() => refetch()} />
      </ScreenContent>
    );
  }

  if (!caseData) {
    return (
      <ScreenContent className="flex items-center justify-center min-h-[300px] p-4">
        <div className="mx-auto flex max-w-[400px] flex-col items-center gap-4 text-center">
          <Title level="3" weight="2" className="m-0">
            {t('notFoundTitle')}
          </Title>
          <Text className="text-white/60">{t('notFoundText')}</Text>
          <Button onClick={() => navigation.navigate('cases')} size="l" stretched>
            {t('backToCases')}
          </Button>
        </div>
      </ScreenContent>
    );
  }

  const TitleTag = seoHeading ? 'p' : 'h1';
  const seoH1 = seoHeading ? <h1 className="sr-only">{seoHeading}</h1> : null;

  if (isDesktop) {
    return (
      <ScreenContent
        scrollRef={scrollRef}
        scrollKey={caseId ? `case-details-${caseId}` : undefined}
      >
        <div className="flex flex-col gap-6 p-6">
          {seoH1}

          <div className="relative rounded-2xl overflow-hidden border border-white/[0.08]">

            <div className="absolute inset-0 scale-[1.5] blur-[20px] pointer-events-none opacity-40">
              <BlurImage
                src={caseData.imageUrl}
                placeholder={caseData.imagePlaceholder}
                alt=""
                className="w-full h-full"
              />
            </div>

            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse 120% 120% at 25% 50%, transparent 30%, rgba(5,5,15,0.4) 100%)',
              }}
            />

            <OverlayBadge
              blur
              className="absolute top-4 end-4 z-10 gap-1.5 px-3 py-1.5 text-white/70 text-sm backdrop-blur-md"
            >
              <GiOpenChest />
              <span className="font-semibold">{formatCreditsShort(caseData.openingsCount)}</span>
            </OverlayBadge>

            <div className="relative z-[1] flex items-center gap-6">

              <div className="relative w-[300px] h-[300px] shrink-0">
                <div
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[70%] h-[30%] blur-[30px] opacity-50 pointer-events-none rounded-full"
                  style={{ background: 'radial-gradient(circle, rgba(100,140,255,0.5) 0%, transparent 70%)' }}
                />
                <BlurImage
                  src={caseData.imageUrl}
                  placeholder={caseData.imagePlaceholder}
                  alt={caseData.name}
                  className="relative w-full h-full drop-shadow-[0_10px_40px_rgba(0,0,0,0.5)]"
                />
              </div>


              <div className="flex flex-col items-center gap-8 flex-1 min-w-0 pe-6">
                <TitleTag
                  className="text-5xl font-bold text-white m-0 text-center"
                  style={{ textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}
                >
                  {caseData.name}
                </TitleTag>

                <div className="flex w-full max-w-[360px] flex-col gap-3">
                  {balanceRow}
                  {qtySelector}
                  {openCta}
                </div>
              </div>
            </div>
          </div>


          <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] items-start gap-2">
            {itemCards}
          </div>
          {similarBlock}

          {seoSlot}
        </div>
      </ScreenContent>
    );
  }

  return (
    <>
      <ScreenDisableSafeArea />
      <ScreenFixedBottom>
        <ButtonGroup>
          {balanceRow}
          {qtySelector}
          {openCta}
        </ButtonGroup>
      </ScreenFixedBottom>
      <ScreenContent
        scrollRef={scrollRef}
        scrollKey={caseId ? `case-details-${caseId}` : undefined}
      >
        <div className="flex flex-col gap-6">
          {seoH1}

          <div className="w-full flex justify-center items-center relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[20%] after:bg-[linear-gradient(to_bottom,transparent,rgba(0,0,0,1))] after:pointer-events-none">
            <BlurImage
              src={caseData.imageUrl}
              placeholder={caseData.imagePlaceholder}
              alt={caseData.name}
              className="w-full aspect-square"
            />
            <div className="absolute bottom-0 start-0 end-0 p-3 flex flex-col items-center gap-1 z-[1]">
              <OverlayBadge>
                <TitleTag className="text-3xl font-semibold text-white">{caseData.name}</TitleTag>
              </OverlayBadge>
              <div className="flex flex-row items-center gap-2">
                <OverlayBadge>
                  {priceTag(caseData.priceInCredits)}
                </OverlayBadge>
                <OverlayBadge>
                  <GiOpenChest style={{ marginRight: '4px' }} />
                  <span className="text-sm font-semibold text-white">
                    {formatCreditsShort(caseData.openingsCount)}
                  </span>
                </OverlayBadge>
              </div>
            </div>
          </div>


          <div className="flex flex-col gap-4">
            <Title level="2" className="text-lg font-semibold text-white m-0 text-center">
              {t('itemsInCase')}
            </Title>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,160px),1fr))] items-start gap-1">
              {itemCards}
            </div>
          </div>

          {similarBlock}

          {seoSlot && <div className="px-4 pb-8">{seoSlot}</div>}
        </div>
      </ScreenContent>
    </>
  );
};
